import React from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import type { AvailableMatch } from '../../hooks/useHomeFeed';

function formatMatchDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === tomorrow.toDateString()) return 'Mañana';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}

interface TopMatchesListProps {
  matches: AvailableMatch[];
  totalCount: number;
  onMatchPress?: (matchId: string) => void;
  onViewAll?: () => void;
}

export default function TopMatchesList({
  matches,
  totalCount = 0,
  onMatchPress,
  onViewAll,
}: TopMatchesListProps) {
  const navigate = useNavigate();

  if (!matches || matches.length === 0) return null;

  const displayed = matches.slice(0, 3);
  const total = totalCount || matches.length;
  const remaining = total - 3;

  const handlePress = (matchId: string) => {
    if (onMatchPress) onMatchPress(matchId);
    else navigate(`/match/${matchId}`);
  };

  const handleViewAll = () => {
    if (onViewAll) onViewAll();
    else navigate('/matches');
  };

  return (
    <div style={{ marginBottom: theme.spacing.md }}>
      <h3 style={{ margin: '0 0 4px 0', color: theme.colors.text, fontFamily: theme.fonts.body, fontWeight: 700 }}>
        Partidos para vos
      </h3>
      <p style={{ margin: '0 0 12px 0', color: theme.colors.textSecondary, fontSize: 13 }}>
        Anotate y jugá!
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
        {displayed.map((match) => (
          <button
            type="button"
            key={match.id}
            tabIndex={0}
            onClick={() => handlePress(match.id)}
            onKeyDown={(e) => e.key === 'Enter' && handlePress(match.id)}
            style={{
              padding: theme.spacing.md,
              backgroundColor: theme.colors.surface,
              borderRadius: theme.borderRadius.card,
              border: `1px solid ${theme.colors.border}`,
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.2s ease',
              background: theme.colors.surface,
              textAlign: 'left',
              width: '100%',
            }}
          >
            {match.badge?.label && (
              <span
                style={{
                  position: 'absolute',
                  top: theme.spacing.sm,
                  right: theme.spacing.sm,
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: `1px solid ${match.badge.color || theme.colors.primary}50`,
                  backgroundColor: `${match.badge.color || theme.colors.primary}20`,
                  color: match.badge.color || theme.colors.primary,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {match.badge.label}
              </span>
            )}
            <div style={{ color: theme.colors.text, fontWeight: 700, fontSize: 16, marginBottom: 4, paddingRight: 100 }}>
              {match.location || 'Ubicación no especificada'}
            </div>
            {match.friendsJoined && match.friendsJoined.length > 0 && (
              <div style={{ color: theme.colors.primary, fontSize: 12, marginBottom: 4 }}>
                👥 {match.friendsJoined[0]}
                {match.friendsJoined.length > 1 ? ` y ${match.friendsJoined.length - 1} más` : ''} confirmado
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, color: theme.colors.textSecondary, fontSize: 12 }}>
              <span>📅 {formatMatchDate(match.date)}, {match.time || '00:00'}</span>
              <span>
                👥 {match.playersJoined}/{match.maxPlayers}
                {match.maxPlayers - match.playersJoined <= 3 &&
                  ` (${match.maxPlayers - match.playersJoined} ${match.maxPlayers - match.playersJoined === 1 ? 'lugar' : 'lugares'})`}
              </span>
            </div>
            <div style={{ textAlign: 'right', marginTop: 4 }}>
              <span style={{ color: theme.colors.primary, fontSize: 12, fontWeight: 600 }}>Ver detalles →</span>
            </div>
          </button>
        ))}
      </div>
      {remaining > 0 && (
        <button
          type="button"
          onClick={handleViewAll}
          style={{
            marginTop: theme.spacing.sm,
            padding: theme.spacing.sm,
            background: 'none',
            border: 'none',
            color: theme.colors.primary,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Ver todos los partidos ({total}) →
        </button>
      )}
    </div>
  );
}
